import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useMouseStore } from '../../../../hooks/stores/mouseStore';

export interface GradientColorItem {
  color: string | undefined;
  stop: number | null;
}

export const GradientSlider = ({
  colorList,
  setColors,
  activeColorIndex,
  setActiveColorIndex,
}: {
  colorList: GradientColorItem[];
  setColors: (newColors: GradientColorItem[]) => void;
  activeColorIndex: number;
  setActiveColorIndex: (index: number) => void;
}) => {
  const [mouseData] = useMouseStore();
  const [hoveredThumbTooltipIndex, setHoveredThumbTooltipIndex] = useState<number | undefined>(undefined);
  const [draggingThumbIndex, setDraggingThumbIndex] = useState<number | undefined>(undefined);
  const [draggingStartValue, setDraggingStartValue] = useState(0);
  const sliderBarRef = useRef<HTMLDivElement>(null);

  const getGradientString = () => {
    let gradientString = 'linear-gradient(to right';
    colorList.forEach((color) => {
      gradientString += `, ${color.color} ${color.stop}%`;
    });
    gradientString += ')';
    return gradientString;
  };

  const removeColorItem = (index: number) => {
    if (colorList.length <= 2) return;
    const newColors = [...colorList];
    newColors.splice(index, 1);

    setColors(newColors);
  };

  const addColorItem = (stop: number) => {
    const newColor = { color: '#000000', stop };

    colorList.push(newColor);
    colorList.sort((a, b) => (a.stop || 0) - (b.stop || 0));

    setColors([...colorList]);
    setActiveColorIndex(colorList.indexOf(newColor));
  };

  const updateColorItemStop = (index: number, stop: number) => {
    if (stop < 0) stop = 0;
    if (stop > 100) stop = 100;

    const newColors = [...colorList];
    newColors[index].stop = stop;

    setColors(newColors);
  };

  // Dragging update value
  useEffect(() => {
    if (!mouseData.draggingDiff || draggingThumbIndex === undefined) return;
    const xDiff = mouseData.draggingDiff.x;
    const newValue = draggingStartValue + Math.round(xDiff * 0.5);

    updateColorItemStop(draggingThumbIndex, newValue);
  }, [draggingThumbIndex, mouseData.draggingDiff]);

  useEffect(() => {
    if (!mouseData.isDown) {
      setDraggingThumbIndex(undefined);
    }
  }, [mouseData.isDown]);

  return (
    <Container>
      <SliderBarCheckboard
        ref={sliderBarRef}
        onClick={() => {
          if (draggingThumbIndex !== undefined) return;

          const rect = sliderBarRef.current!.getBoundingClientRect();
          const stop = Math.round(((mouseData.pos.x - rect.left) / rect.width) * 100);

          addColorItem(stop);
        }}
        style={{
          cursor: draggingThumbIndex !== undefined ? 'default' : undefined,
        }}
      >
        <SliderBarFill style={{ background: getGradientString() }} />
        {colorList.map((colorItem, index) => (
          <ThumbCheckboard
            $isActive={activeColorIndex === index}
            onMouseDown={() => {
              setActiveColorIndex(index);
              setDraggingThumbIndex(index);
              setDraggingStartValue(colorItem.stop || 0);
            }}
            onClick={(e) => e.stopPropagation()}
            key={'colorThumb-' + index}
            style={{ left: colorItem.stop + '%' }}
          >
            <ThumbFill key={'colorThumbFill-' + index} style={{ background: colorItem.color }} />

            <StopTooltip
              $isActive={activeColorIndex === index}
              onMouseEnter={() => colorList.length > 2 && setHoveredThumbTooltipIndex(index)}
              onMouseLeave={() => setHoveredThumbTooltipIndex(undefined)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                removeColorItem(index);
              }}
              style={{
                pointerEvents: colorList.length <= 2 ? 'none' : undefined,
              }}
            >
              {hoveredThumbTooltipIndex === index ? <i className="icon-x" /> : colorItem.stop}
            </StopTooltip>
          </ThumbCheckboard>
        ))}
      </SliderBarCheckboard>
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 70px;
  display: flex;
  padding: 7px 7px;
`;

const SliderBarCheckboard = styled.div`
  cursor: copy;
  position: relative;
  width: 100%;
  height: 24px;
  display: flex;
  align-items: center;

  border-radius: var(--radius-1);
  background: url('data:image/svg+xml;utf8,<svg preserveAspectRatio="none"  viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><rect x="0" y="0" width="5" height="5" fill="grey" /><rect x="5" y="5" width="5" height="5" fill="grey" /><rect x="5" y="0" width="5" height="5" fill="white" /><rect x="0" y="5" width="5" height="5" fill="white" /></svg>')
    0 0/12px 12px;
`;

const SliderBarFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  border-radius: var(--radius-1);
`;

const ThumbCheckboard = styled.div<{ $isActive?: boolean }>`
  cursor: default;
  position: absolute;
  left: 0;
  width: 8px;
  height: 27px;
  translate: -50%;

  display: flex;
  justify-content: center;

  border-radius: 5px;
  border: ${({ $isActive }) => ($isActive ? '2px' : '1px')} solid var(--main-1);
  box-shadow: var(--shadow-1);
  background: url('data:image/svg+xml;utf8,<svg preserveAspectRatio="none"  viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><rect x="0" y="0" width="5" height="5" fill="grey" /><rect x="5" y="5" width="5" height="5" fill="grey" /><rect x="5" y="0" width="5" height="5" fill="white" /><rect x="0" y="5" width="5" height="5" fill="white" /></svg>')
    0 0/8px 8px;

  &:hover {
    border-width: 2px;
  }

  ${({ $isActive }) => $isActive && `z-index: 1;`}
`;

const ThumbFill = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;

  border-radius: 5px;
`;

const StopTooltip = styled.div<{ $isActive?: boolean }>`
  position: absolute;
  bottom: 0;
  width: 15px;
  cursor: pointer;
  padding: 3px 5px;
  translate: 0 calc(100% + 7px);

  display: flex;
  justify-content: center;

  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);
  background-color: var(--neutral-1);
  color: var(--main-2);
  font-size: 11px;
  box-shadow: var(--shadow-1);

  transition: background var(--transition-2);

  &:hover {
    color: var(--main-1);
    background-color: var(--neutral-3);
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    color: var(--main-1);
    background-color: var(--neutral-3);
  `}
`;
